import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import ScrollToTop from './components/ScrollToTop';
import MarketingLayout from './components/MarketingLayout';
import Login from './pages/Login';
import Signup from './pages/Signup';
import SignupSuccess from './pages/SignupSuccess';
import VerifyEmail from './pages/VerifyEmail';
import LoggedOut from './pages/LoggedOut';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import LandingPage from './pages/public/LandingPage';
import PricingPage from './pages/public/PricingPage';
import FeaturesPage from './pages/public/FeaturesPage';
import HowItWorksPage from './pages/public/HowItWorksPage';
import Dashboard from './pages/Dashboard';
import FrontDesk from './pages/FrontDesk';
import Calendar from './pages/Calendar';
import Reservations from './pages/Reservations';
import Housekeeping from './pages/Housekeeping';
import Guests from './pages/Guests';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import RatePlans from './pages/RatePlans';
import ChannelManager from './pages/ChannelManager';
import RevenueAnalytics from './pages/RevenueAnalytics';
import BookingEngine from './pages/BookingEngine';
import AddOns from './pages/AddOns';
import PromoCodes from './pages/PromoCodes';
import Messaging from './pages/Messaging';
import GuestPortal from './pages/GuestPortal';
import Staff from './pages/Staff';
import Maintenance from './pages/Maintenance';
import AuditLogs from './pages/AuditLogs';
import Forecasting from './pages/Forecasting';
import ExportReports from './pages/ExportReports';
import Accounting from './pages/Accounting';
import Communications from './pages/Communications';
import PaymentDiversity from './pages/PaymentDiversity';
import SmartSystems from './pages/SmartSystems';
import MobileStaff from './pages/MobileStaff';
import MobileGuest from './pages/MobileGuest';
import TabletPOS from './pages/TabletPOS';
import Enterprise from './pages/Enterprise';
// APIMarketplace removed - no longer exposed to users
import AdvancedAnalytics from './pages/AdvancedAnalytics';
import UserManagement from './pages/UserManagement';
import DNRManagement from './pages/DNRManagement';
import PlatformAdmin from './pages/PlatformAdmin';
import DatabaseAdmin from './pages/DatabaseAdmin';
import AdminOrgDetail from './pages/AdminOrgDetail';
import AdminPropertyDetail from './pages/AdminPropertyDetail';
import Layout from './components/Layout';
import { Toaster } from 'sonner';
import axios from 'axios';
import './App.css';

// Component to setup axios interceptors with access to navigate
function AxiosInterceptorSetup({ handleLogout }) {
  const navigate = useNavigate();
  
  useEffect(() => {
    // Setup response interceptor
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Handle 401 Unauthorized - token expired
        if (error.response?.status === 401) {
          console.log('[Axios] 401 Unauthorized - Token expired, logging out');
          handleLogout();
          navigate('/login');
        }
        return Promise.reject(error);
      }
    );
    
    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [navigate, handleLogout]);
  
  return null;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData, tokens) => {
    localStorage.setItem('access_token', tokens.access_token);
    localStorage.setItem('refresh_token', tokens.refresh_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('selected_property');
    setUser(null);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <>
      <BrowserRouter>
        <AxiosInterceptorSetup handleLogout={handleLogout} />
        <ScrollToTop />
        <Routes>
          {/* Public Marketing Routes - With Header & Footer */}
          <Route element={<MarketingLayout />}>
            <Route path="/" element={!user ? <LandingPage /> : <Navigate to="/dashboard" />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/login" element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/dashboard" />} />
            <Route path="/signup" element={!user ? <Signup /> : <Navigate to="/dashboard" />} />
            <Route path="/signup-success" element={<SignupSuccess />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/logged-out" element={<LoggedOut />} />
          </Route>
          
          {/* Standalone Public Routes (no footer) */}
          <Route path="/book" element={<BookingEngine />} />
          <Route path="/guest-portal" element={<GuestPortal />} />
          
          {/* Protected PMS Application Routes - NO Marketing Footer */}
          <Route path="/dashboard" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={user?.role === 'front_desk' ? <FrontDesk /> : <Dashboard />} />
          </Route>
          <Route path="/front-desk" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<FrontDesk />} />
          </Route>
          <Route path="/calendar" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<Calendar />} />
          </Route>
          <Route path="/reservations" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<Reservations />} />
          </Route>
          <Route path="/housekeeping" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<Housekeeping />} />
          </Route>
          <Route path="/guests" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<Guests />} />
          </Route>
          <Route path="/reports" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<Reports />} />
          </Route>
          <Route path="/rate-plans" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<RatePlans />} />
          </Route>
          <Route path="/channel-manager" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<ChannelManager />} />
          </Route>
          <Route path="/revenue-analytics" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<RevenueAnalytics />} />
          </Route>
          <Route path="/addons" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<AddOns />} />
          </Route>
          <Route path="/promo-codes" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<PromoCodes />} />
          </Route>
          <Route path="/messaging" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<Messaging />} />
          </Route>
          <Route path="/staff" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<Staff />} />
          </Route>
          <Route path="/maintenance" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<Maintenance />} />
          </Route>
          <Route path="/audit-logs" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<AuditLogs />} />
          </Route>
          <Route path="/forecasting" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<Forecasting />} />
          </Route>
          <Route path="/export-reports" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<ExportReports />} />
          </Route>
          <Route path="/accounting" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<Accounting />} />
          </Route>
          <Route path="/communications" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<Communications />} />
          </Route>
          <Route path="/payment-diversity" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<PaymentDiversity />} />
          </Route>
          <Route path="/smart-systems" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<SmartSystems />} />
          </Route>
          <Route path="/mobile-staff" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<MobileStaff />} />
          </Route>
          <Route path="/mobile-guest" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<MobileGuest />} />
          </Route>
          {/* Tablet POS - Hidden from navigation but route kept for future use */}
          {/* <Route path="/tablet-pos" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<TabletPOS />} />
          </Route> */}
          {/* Enterprise - Hidden from navigation but route kept for future use */}
          {/* <Route path="/enterprise" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<Enterprise />} />
          </Route> */}
          {/* API Marketplace route removed - no longer customer-facing */}
          <Route path="/advanced-analytics" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<AdvancedAnalytics />} />
          </Route>
          <Route path="/user-management" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<UserManagement />} />
          </Route>
          <Route path="/dnr" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<DNRManagement />} />
          </Route>
          <Route path="/platform-admin" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<PlatformAdmin />} />
          </Route>
          <Route path="/database-admin" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<DatabaseAdmin />} />
          </Route>
          <Route path="/admin-org/:orgId" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<AdminOrgDetail />} />
          </Route>
          <Route path="/admin-property/:orgId/:propertyId" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<AdminPropertyDetail />} />
          </Route>
          <Route path="/settings" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<Settings />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" expand={false} richColors />
    </>
  );
}

export default App;
